import React, { useState, useEffect } from 'react';
import { X, Calendar, User, FileText, CheckCircle, XCircle, Package, UserPlus, UserMinus, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const HistoryModal = ({ isOpen, onClose, equipment }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  // API Base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Fetch equipment history when modal opens
  useEffect(() => {
    if (isOpen && equipment?.id) {
      fetchEquipmentHistory();
    }
  }, [isOpen, equipment?.id]);

  const fetchEquipmentHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      const response = await axios.get(`${API_BASE_URL}/safetyequipment/${equipment.id}/history/`, config);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error fetching equipment history:', error);
      setError(error.response?.data?.detail || 'Failed to fetch equipment history');
    } finally {
      setLoading(false);
    }
  };

  // Get icon based on event type
  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'created':
        return <Package className="h-5 w-5 text-blue-600" />;
      case 'assignment_created':
        return <UserPlus className="h-5 w-5 text-green-600" />;
      case 'assignment_returned':
        return <UserMinus className="h-5 w-5 text-orange-600" />;
      case 'status_changed':
        return <Clock className="h-5 w-5 text-purple-600" />;
      case 'inspection':
        return <Eye className="h-5 w-5 text-indigo-600" />;
      case 'inspection_passed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'inspection_failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  // Get color class based on event type
  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'created':
        return 'bg-blue-50 border-blue-200';
      case 'assignment_created':
        return 'bg-green-50 border-green-200';
      case 'assignment_returned':
        return 'bg-orange-50 border-orange-200';
      case 'status_changed':
        return 'bg-purple-50 border-purple-200';
      case 'inspection':
        return 'bg-indigo-50 border-indigo-200';
      case 'inspection_passed':
        return 'bg-green-50 border-green-200';
      case 'inspection_failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Equipment History</h2>
            {equipment && (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{equipment.safety_equipment_type_name}</span>
                {equipment.safety_equipment_model_brand && equipment.safety_equipment_model_name && (
                  <span> - {equipment.safety_equipment_model_brand} {equipment.safety_equipment_model_name}</span>
                )}
                {equipment.safety_equipment_serial_number && (
                  <span> (#{equipment.safety_equipment_serial_number})</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-gray-600">Loading history...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p className="font-bold">Error</p>
                <p>{error}</p>
              </div>
              <button 
                onClick={fetchEquipmentHistory}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Retry
              </button>
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No history available for this equipment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {historyData.map((event, index) => (
                <div 
                  key={index}
                  className={`border rounded-lg p-4 ${getEventColor(event.event_type)}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getEventIcon(event.event_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">
                          {event.title}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {formatDate(event.timestamp)}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {event.description}
                        </p>
                      )}
                      {event.user && (
                        <div className="flex items-center mt-2 text-xs text-gray-500">
                          <User className="h-3 w-3 mr-1" />
                          <span>by {event.user}</span>
                        </div>
                      )}
                      {event.details && Object.keys(event.details).length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {Object.entries(event.details).map(([key, value]) => (
                            <div key={key} className="flex">
                              <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                              <span className="ml-1">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;